export type AuthAction = 'EDIT' | 'VIEW' | 'READ' | 'DELETE' | 'CREATE' | 'UPDATE' | 'EXPORT' | 'MANAGE' | 'ACCESS';

export interface IPermissionActionDescriptor {
    resource: string;
    resourceType: string;
    action: AuthAction;
    canViewAdmin?: boolean;
    description?: string;
}

export const PermissionAction = {
    /**
     * Unified Audit (AuthZ: ResourceGroup Audit). Two resource types rather than one action at
     * two scopes: a grant at `/` prefix-matches every `/tenant/<id>`, so the resource type is the
     * only thing that keeps `Org Auditor` out of tenant audit events.
     */
    Audit: {
        AuditLogs: {
            View: {
                resource: 'Audit',
                resourceType: 'AuditLogs',
                action: 'VIEW',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
        },
        TenantAuditLogs: {
            View: {
                resource: 'Audit',
                resourceType: 'TenantAuditLogs',
                action: 'VIEW',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
        },
    },
    CentralizedAccess: {
        AdministrationPage: {
            View: {
                resource: 'CentralizedAccess',
                resourceType: 'AdministrationPage',
                action: 'VIEW',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Edit: {
                resource: 'CentralizedAccess',
                resourceType: 'AdministrationPage',
                action: 'EDIT',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Create: {
                resource: 'CentralizedAccess',
                resourceType: 'AdministrationPage',
                action: 'CREATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Delete: {
                resource: 'CentralizedAccess',
                resourceType: 'AdministrationPage',
                action: 'DELETE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
        },
        Role: {
            Read: {
                resource: 'Role',
                resourceType: 'Role',
                action: 'READ',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Create: {
                resource: 'Role',
                resourceType: 'Role',
                action: 'CREATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Update: {
                resource: 'Role',
                resourceType: 'Role',
                action: 'UPDATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Delete: {
                resource: 'Role',
                resourceType: 'Role',
                action: 'DELETE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
        },
        RoleAssignment: {
            Read: {
                resource: 'Role Assignment',
                resourceType: 'RoleAssignment',
                action: 'READ',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Create: {
                resource: 'Role Assignment',
                resourceType: 'RoleAssignment',
                action: 'CREATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Update: {
                resource: 'Role Assignment',
                resourceType: 'RoleAssignment',
                action: 'UPDATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Delete: {
                resource: 'Role Assignment',
                resourceType: 'RoleAssignment',
                action: 'DELETE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Export: {
                resource: 'Role Assignment',
                resourceType: 'RoleAssignment',
                action: 'EXPORT',
                canViewAdmin: true,
                description: 'Export role assignment data',
            } as IPermissionActionDescriptor,
        },
    },
    Identity: {
        User: {
            Read: {
                resource: 'Identity',
                resourceType: 'User',
                action: 'READ',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Create: {
                resource: 'Identity',
                resourceType: 'User',
                action: 'CREATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Update: {
                resource: 'Identity',
                resourceType: 'User',
                action: 'UPDATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Delete: {
                resource: 'Identity',
                resourceType: 'User',
                action: 'DELETE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
        },
        Group: {
            Read: {
                resource: 'Identity',
                resourceType: 'Group',
                action: 'READ',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Create: {
                resource: 'Identity',
                resourceType: 'Group',
                action: 'CREATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Update: {
                resource: 'Identity',
                resourceType: 'Group',
                action: 'UPDATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Delete: {
                resource: 'Identity',
                resourceType: 'Group',
                action: 'DELETE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
        },
        RobotAccount: {
            Read: {
                resource: 'Identity',
                resourceType: 'RobotAccount',
                action: 'READ',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Create: {
                resource: 'Identity',
                resourceType: 'RobotAccount',
                action: 'CREATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Update: {
                resource: 'Identity',
                resourceType: 'RobotAccount',
                action: 'UPDATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Delete: {
                resource: 'Identity',
                resourceType: 'RobotAccount',
                action: 'DELETE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
        },
    },
    /**
     * Licensing service (AuthZ: ResourceGroup Licensing, ResourceType LicensingData).
     * Viewer role typically has View; allocation / migration / trials require Manage.
     */
    Licensing: {
        LicensingData: {
            View: {
                resource: 'Licensing',
                resourceType: 'LicensingData',
                action: 'VIEW',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Access: {
                resource: 'Licensing',
                resourceType: 'LicensingData',
                action: 'ACCESS',
                canViewAdmin: false,
            } as IPermissionActionDescriptor,
            Manage: {
                resource: 'Licensing',
                resourceType: 'LicensingData',
                action: 'MANAGE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
        },
        /**
         * Tenant-scoped read of allocations + consumption views. Granted via the
         * `Tenant Licensing Viewer` role at tenant scope; also rolled into
         * org-scoped `Licensing Viewer`/`Licensing Administrator` and `Tenant Administrator`.
         */
        TenantLicensingData: {
            View: {
                resource: 'Licensing',
                resourceType: 'TenantLicensingData',
                action: 'VIEW',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
        },
    },
    // Relay is administered from the tenant admin UI; these descriptors gate the Relay tile and
    // each relay group / client action so the UI matches what the Relay service actually allows.
    Relay: {
        RelayGroup: {
            View: {
                resource: 'Relay',
                resourceType: 'RelayGroup',
                action: 'VIEW',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Create: {
                resource: 'Relay',
                resourceType: 'RelayGroup',
                action: 'CREATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Edit: {
                resource: 'Relay',
                resourceType: 'RelayGroup',
                action: 'EDIT',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Delete: {
                resource: 'Relay',
                resourceType: 'RelayGroup',
                action: 'DELETE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
        },
        RelayClient: {
            Delete: {
                resource: 'Relay',
                resourceType: 'RelayClient',
                action: 'DELETE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
        },
        RelayGroupConfig: {
            View: {
                resource: 'Relay',
                resourceType: 'RelayGroupConfig',
                action: 'VIEW',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
        },
    },
} as const;

/**
 * VIEW or MANAGE on `LicensingData` — used for license pages, org/tenant license cards, and related UI.
 * **ACCESS** is intentionally excluded: Access-only users must not use licensing admin or license routes.
 */
export const getLicensingViewOrManagePermissions = (): IPermissionActionDescriptor[] => [
    PermissionAction.Licensing.LicensingData.View,
    PermissionAction.Licensing.LicensingData.Manage,
];

/**
 * Tenant-scoped Licensing read permission, used to gate the tenant Licenses card and
 * tenant licensing routes for users holding the `Tenant Licensing Viewer` role.
 * Org-scoped Licensing Viewer/Admin also carry this descriptor.
 */
export const getTenantLicensingViewPermissions = (): IPermissionActionDescriptor[] => [
    PermissionAction.Licensing.TenantLicensingData.View,
];

/**
 * Organization-scoped Unified Audit read permission, granted by the `Org Auditor` role.
 * Deliberately excludes the tenant action: an Org Auditor must not read tenant audit events.
 */
export const getAuditOrgViewPermissions = (): IPermissionActionDescriptor[] => [
    PermissionAction.Audit.AuditLogs.View,
];

/**
 * Tenant-scoped Unified Audit read permission, granted by `Tenant Auditor` and `Tenant Administrator`.
 * Always pass the route's tenantId to {@link useHasAnyPermission} so a grant on tenant A does not
 * satisfy a check for tenant B.
 */
export const getAuditTenantViewPermissions = (): IPermissionActionDescriptor[] => [
    PermissionAction.Audit.TenantAuditLogs.View,
];

/**
 * Returns all PermissionAction descriptors under a given resource group.
 * e.g. getPermissionsForGroup('Identity') returns all User, Group, and RobotAccount permissions.
 */
export const getPermissionsForGroup = (
    groupName: keyof typeof PermissionAction,
): IPermissionActionDescriptor[] => {
    const group = PermissionAction[groupName];
    const permissions: IPermissionActionDescriptor[] = [];
    for (const type of Object.values(group)) {
        for (const action of Object.values(type)) {
            permissions.push(action as IPermissionActionDescriptor);
        }
    }
    return permissions;
};

/**
 * Formats a permission descriptor into dot notation for display.
 * e.g. { resource: 'identity', resourceType: 'LicensingData', action: 'READ' } => 'Identity.LicensingData.Read'
 *
 * Resource and resourceType are PascalCase in the descriptors, so only their first letter is
 * normalized; the action is stored UPPERCASE and is fully normalized (e.g. 'MANAGE' => 'Manage').
 */
export const formatPermissionName = (descriptor: IPermissionActionDescriptor): string =>
    `${capitalizeFirst(descriptor.resource)}.${capitalizeFirst(descriptor.resourceType)}.${capitalize(descriptor.action)}`;

const capitalizeFirst = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

/**
 * Returns all PermissionAction descriptors that have canViewAdmin: true.
 * Used by UserAuthPDPModelProvider to determine if a user should get admin route access.
 */
export const getAdminViewPermissions = (): IPermissionActionDescriptor[] => {
    const permissions: IPermissionActionDescriptor[] = [];
    for (const group of Object.values(PermissionAction)) {
        for (const type of Object.values(group)) {
            for (const action of Object.values(type)) {
                if ((action as IPermissionActionDescriptor).canViewAdmin) {
                    permissions.push(action as IPermissionActionDescriptor);
                }
            }
        }
    }
    return permissions;
};
